/**
 * User-initiated disconnect orchestration for the web app.
 *
 * Mirrors the extension's `handleDisconnect` in
 * `apps/extension/src/background/index.ts`. The local-state cleanup
 * is split out as `clearLocalState` so the SSE-side `pairing_lost`
 * handler in `services/sse.ts` (the web's mirror of the extension's
 * `handlePairingLost`) can run the wipe without re-hitting
 * `DELETE /pair` — the server already knows the device is gone in
 * that path.
 *
 * Order matters:
 *   1. Best-effort `unregisterWebPushSubscription` so the server
 *      stops dispatching push events. The server-side
 *      `device_subscriptions` row would be cleaned up by the
 *      `ON DELETE CASCADE` triggered by `DELETE /pair` anyway, but
 *      the explicit DELETE also fires the
 *      `web_push_subscription_deleted` analytics event the cascade
 *      can't see. Failures are logged + swallowed.
 *   2. Best-effort `DELETE /v1/devices/<self>` (V1 multi-device).
 *      Drops only THIS browser from the pair set. The legacy
 *      `DELETE /pair` below would otherwise unlink every partner
 *      of the calling device in a single transaction, silently
 *      regressing other PCs paired with the same phone. Failures
 *      are logged + swallowed; the legacy unpair below still runs.
 *      Skipped when `deviceId` is absent in storage (pre-multi-device
 *      installs that never wrote the key).
 *   3. `DELETE /pair` — load-bearing. If this fails we ABORT the
 *      flow before clearing local state so the user can retry
 *      against the same token rather than losing auth material to
 *      a server that still thinks this device is paired.
 *   4. `pushManager.unsubscribe()` so the browser stops receiving
 *      pushes for the now-deleted subscription. Otherwise the SW
 *      would keep firing push events with `unpaired` outcomes
 *      (harmless but noisy).
 *   5. `clearExcept(["publicKey","secretKey"])` wipes every key EXCEPT
 *      the device keypair (token, phonePubKey, conversations,
 *      notifications, …). The page-side `useStorage` listeners fire
 *      `paired: undefined` and the router falls back to PairingPage
 *      automatically. The keypair survives by design — see
 *      `clearLocalState` below for why.
 *   6. `applyBadge(0)` clears the title prefix + favicon canvas +
 *      app badge so the post-disconnect tab doesn't keep advertising
 *      a stale unread count.
 *
 * Steps 4-6 are the "local cleanup" group. If any of them throws
 * AFTER step 3 already landed, we report `success: true,
 * localCleanupOk: false` so the SettingsPage can suggest a reload
 * rather than a misleading retry — the server is already unlinked.
 *
 * SW lifecycle: we deliberately do NOT call
 * `registration.unregister()`. The SW is a singleton per origin —
 * leaving it installed is the right default, and the next pair would
 * just have to re-register. The SW already gracefully no-ops on
 * push events when keys are missing
 * (`PushHandlerOutcome.unpaired`).
 */

import { deleteAllOpfsTransfers } from "@reflecto/client-core";
import { clearExcept, webStorage } from "../adapters/storage.web";
import { disconnectDevice, removeDevice, unregisterWebPushSubscription } from "../api/client";
import { applyBadge } from "../lib/badge";
import { logError, logWarn } from "../lib/log";
import { clearClips } from "./clipsStore";

export interface DisconnectResult {
  /**
   * `true` once the server has unlinked the device — i.e. the
   * `DELETE /pair` round-trip succeeded OR the token was missing
   * (already disconnected). Local cleanup may still have failed
   * after that point; check `localCleanupOk` for the full picture.
   */
  success: boolean;
  /**
   * `false` when one of the local cleanup steps (push unsubscribe,
   * IDB clear, badge clear) threw after the server unpair already
   * landed. The user is unpaired from the server's perspective but
   * the page still has stale auth material in IndexedDB — they
   * should reload to recover. Distinct from `success` so the UI can
   * show a "Disconnected — please reload to clear local data" hint
   * instead of a misleading "Failed to disconnect" toast that would
   * tempt the user to retry against an already-invalidated token.
   */
  localCleanupOk: boolean;
  /** Human-readable detail when either signal is false. */
  error?: string;
}

/**
 * Best-effort `pushManager.unsubscribe()`. Logs and swallows every
 * failure mode — the cleanup must continue even if the browser
 * rejects the unsubscribe (no SW registered, no active subscription,
 * SecurityError on partitioned storage, etc.).
 */
async function unsubscribePush(): Promise<void> {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;
  try {
    const registration = await navigator.serviceWorker.getRegistration();
    if (!registration) return;
    const sub = await registration.pushManager.getSubscription();
    if (!sub) return;
    await sub.unsubscribe();
  } catch (err) {
    logWarn("ui", "push_unsubscribe_failed", err);
  }
}

/**
 * Local-state cleanup. Idempotent and safe to call from any path
 * that needs to drop the paired identity (user disconnect, server-
 * initiated `pairing_lost`, force-reset diagnostics).
 *
 * See `services/pairingLifecycle.ts`'s docblock for the full
 * credential-transition ownership map, including the token-identity
 * guard that decides whether a server-initiated caller should reach
 * this function at all.
 */
export async function clearLocalState(): Promise<void> {
  // Drop the browser-side push subscription first. If we cleared
  // storage first the SW would still be receiving pushes against a
  // subscription we no longer own; the dispatch handler would log
  // an `unpaired` outcome on every one until the OS GC-d the
  // subscription. Order matters.
  await unsubscribePush();
  // Wipe everything EXCEPT the device keypair and the analytics opt-out —
  // see clearExcept for why the keypair matters; the opt-out must survive
  // unpair or a re-pair would silently re-enable analytics against the
  // user's recorded choice. Token, phonePubKey, the self deviceId, and the
  // V1 per-device prefs (hiddenApps, dndSchedule, dndSnoozeUntil,
  // peerDevices, selfDeviceName, pairAutoNameResult, pairConfirmDismissedAt)
  // all go — re-pair starts clean; publicMode is in-memory only.
  await clearExcept(["publicKey", "secretKey", "analyticsOptOut", "internalTester"]);
  // Clip history now persists in IndexedDB via its own `clipHistory` key
  // (spec C1/C5), separate from the `clearExcept` wipe above — wipe it
  // explicitly so a re-pair starts with empty history and no stale sent/
  // received clip survives a disconnect. `clearClips()`'s synchronous
  // prefix (mirror reset + notify) runs before its first internal await
  // regardless, but await the full call so the persisted write has
  // actually landed before this function returns.
  await clearClips();
  // `ftSenderStates`/`ftReceiverStates` are already caught by `clearExcept`
  // above (docs/file-transfer-resume.md §3.1.3) — but the OPFS `ft/`
  // directory (the write-through source copies + partial receive outputs)
  // lives outside IndexedDB and must be wiped explicitly, same as the
  // extension's `handlePairingLost`.
  await deleteAllOpfsTransfers();
  applyBadge(0);
}

/**
 * User-initiated disconnect from the SettingsPage. Tells the server,
 * then runs the local cleanup. Returns a `DisconnectResult` so the
 * caller can branch on the precise mixed-success outcome:
 *   - `success: true,  localCleanupOk: true`  — fully clean.
 *   - `success: true,  localCleanupOk: false` — server unlinked but
 *     IDB clear / push unsubscribe blew up; UI should suggest a
 *     reload.
 *   - `success: false` — server unpair itself failed AND the token
 *     was present. UI shows "Failed to disconnect, retry"; the
 *     local cleanup also DOES NOT RUN in that case so the auth
 *     material stays intact for the retry.
 */
export async function disconnect(): Promise<DisconnectResult> {
  let token: string | undefined;
  let deviceId: string | undefined;
  try {
    ({ token, deviceId } = await webStorage.get("token", "deviceId"));
  } catch (err) {
    // Reading the token is an IDB call — if it itself throws we
    // can't even attempt the server unpair. Treat as a hard fail.
    logError("ui", "disconnect_failed", err);
    return {
      success: false,
      localCleanupOk: false,
      error: err instanceof Error ? err.message : String(err),
    };
  }

  // No token = already disconnected from this side. Still run the
  // local cleanup defensively in case of a partial earlier flow.
  if (!token) {
    return runLocalCleanup();
  }

  // Best-effort server unregister — failures DO NOT abort the flow
  // because the device_subscriptions row will be cleaned up by the
  // ON DELETE CASCADE on `DELETE /pair`. The explicit DELETE just
  // fires the analytics event the cascade can't.
  try {
    await unregisterWebPushSubscription(token);
  } catch (err) {
    logWarn("ui", "web_push_unregister_failed", err);
  }

  // V1 multi-device per-device removal — drop ONLY this device from
  // the user's pair set. `DELETE /pair` (the legacy unpair below)
  // would unlink every partner of the calling device in a single
  // transaction, silently regressing other PCs paired with the same
  // phone. Best-effort: if it fails we still fall through to the
  // legacy call so the local cleanup runs.
  if (deviceId) {
    try {
      await removeDevice(token, deviceId);
    } catch (err) {
      logWarn("ui", "remove_self_device_failed", err);
    }
  }

  // The unpair call IS load-bearing — if it fails the server still
  // thinks this device is paired. Bail BEFORE clearing local state
  // so the user can retry without losing the auth material.
  try {
    await disconnectDevice(token);
  } catch (err) {
    logError("ui", "disconnect_device_failed", err);
    return {
      success: false,
      localCleanupOk: false,
      error: err instanceof Error ? err.message : String(err),
    };
  }

  return runLocalCleanup();
}

/**
 * Run the post-unpair local cleanup and synthesise the result. The
 * server is already unlinked at this point, so a local-cleanup
 * failure becomes `success: true, localCleanupOk: false` rather
 * than a hard failure — the UI can then prompt the user to reload.
 */
async function runLocalCleanup(): Promise<DisconnectResult> {
  try {
    await clearLocalState();
    return { success: true, localCleanupOk: true };
  } catch (err) {
    logError("ui", "disconnect_local_cleanup_failed", err);
    return {
      success: true,
      localCleanupOk: false,
      error: err instanceof Error ? err.message : String(err),
    };
  }
}
